<script setup lang="ts">
import {computed, onUnmounted, ref, watch} from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import WorldSetupFlow from './world/WorldSetupFlow.vue';
import CharacterSetupFlow from './character/CharacterSetupFlow.vue';
import {useModal} from "@/composables/useModal";
import router from '@/router';

const ux = useUxData();
const store = useAppDataStore();
const setupStore = useGuidedSetupStore();

const activeFlow = computed(() => setupStore.activeFlow);

const modalRef = ref<HTMLElement | null>(null);

// A flow can start from the editor (App's Create menu). Cancelling it there leaves the editor on
// the character list: the store is reset first, so the reused view stays on the list.
const onCancelFlow = () => {
    const inEditor = router.currentRoute.value.name === 'character';
    setupStore.resetFlow();
    if (inEditor) {
        store.resetCharacterEditor();
        router.push({ name: 'characters', params: { system: store.system?.alias ?? import.meta.env.VITE_SYSTEM_ALIAS } });
    }
};

// Told only about this modal's own events (see useModal): a modal nested in its body would
// otherwise close the flow when it closed itself.
const { show, hide } = useModal(modalRef, {
    // The modal (and the step content) is now visible — safe to initialize Kendo widgets that
    // measure their container (e.g. the description Editor's adaptive toolbar). Initializing them
    // while the modal was still display:none left the toolbar blank.
    onShown: () => setupStore.setFlowReady(true),
    // Catches dismissal paths that bypass onCancelFlow (e.g., a future code path that
    // hides the modal directly). Keeps the store in sync with the visible modal state.
    onHidden: () => {
        if (setupStore.activeFlow !== null) {
            setupStore.resetFlow();
        }
    },
});

watch(activeFlow, (flow) => {
    if (flow) {
        show();
    } else {
        hide();
    }
});

// Signing out unmounts the modal without closing it (useModal tells no one about that close), so
// onHidden never runs. A flow left active would make starting the same flow after signing in again
// change nothing, and the modal would never open.
onUnmounted(() => {
    if (setupStore.activeFlow !== null) {
        setupStore.resetFlow();
    }
});
</script>

<template>
    <div class="guided-setup">
        <div
            ref="modalRef"
            id="guided-setup-modal"
            class="modal fade"
            tabindex="-1"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            aria-labelledby="guided-setup-modal-title"
        >
            <div class="modal-dialog modal-fullscreen">
                <div class="modal-content">
                    <div class="modal-header header">
                        <h5 id="guided-setup-modal-title" class="modal-title mb-0">
                            <span v-if="activeFlow === 'world'">
                                <span class="fa-light fa-globe me-2"></span>
                                {{ setupStore.flowTitle }}
                            </span>
                            <span v-else-if="activeFlow === 'character'">
                                <span class="fa-light fa-person-rays me-2"></span>
                                {{ setupStore.flowTitle }}
                            </span>
                        </h5>
                        <button
                            type="button"
                            class="btn btn-sm btn-link"
                            data-bs-dismiss="modal"
                            :aria-label="ux.labels?.actions?.cancel ?? 'Cancel'"
                            @click="onCancelFlow"
                        >
                          <span class="fa-light fa-fw fa-times"></span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <WorldSetupFlow v-if="activeFlow === 'world'" />
                        <CharacterSetupFlow v-else-if="activeFlow === 'character'" />
                    </div>
                    <!-- Receives BuildStats and the stepper-footer (Back/Next/Submit)
                         from GuidedStepper via <Teleport>. flex-column stacks them
                         vertically; p-0 so the inner blocks own their own padding. -->
                    <div
                        id="guided-setup-modal-footer"
                        class="modal-footer"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>
